/**
 * NotificationBell Component - Displays notification bell with badge and dropdown
 * Shows unread alerts count and recent alerts in dropdown
 * Enhanced with real-time subscriptions and click tracking
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  Eye,
  TrendingUp,
  Radio,
  Target,
  Calendar,
  ArrowUp,
  ArrowDown,
  User,
  AlertTriangle,
  Briefcase,
  Check,
  CheckCheck,
  Settings
} from 'lucide-react';
import alertService, { ALERT_TYPE_CONFIG } from '../services/alertService';
import { useUnreadCount, useRealtimeAlerts } from '../hooks/useAlerts';
import { formatTimeAgo } from '../utils/alertHelpers';

// Icon mapping for alert types
const ICON_MAP = {
  eye: Eye,
  'trending-up': TrendingUp,
  radio: Radio,
  target: Target,
  calendar: Calendar,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  user: User,
  'alert-triangle': AlertTriangle,
  briefcase: Briefcase,
  bell: Bell
};

// Color classes for priorities
const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-100 text-red-600'
};

// Color classes for alert type badges
const TYPE_COLORS = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  pink: 'bg-pink-500',
  red: 'bg-red-500',
  yellow: 'bg-yellow-500',
  indigo: 'bg-indigo-500',
  gray: 'bg-gray-500'
};

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Use real-time unread count hook
  const { count: unreadCount, decrementCount, resetCount, refresh: refreshCount } = useUnreadCount();

  // Use real-time alerts subscription
  const { latestAlert } = useRealtimeAlerts({
    onNewAlert: (newAlert) => {
      // Add new alert to the top of the list if dropdown is open
      if (isOpen) {
        setAlerts(prev => [newAlert, ...prev.slice(0, 9)]);
      }
    },
    playSound: true,
    showBrowserNotification: false
  });

  // Fetch alerts when dropdown opens
  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const result = await alertService.getAlerts({ limit: 10 });
      setAlerts(result.alerts || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch alerts when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchAlerts();
    }
  }, [isOpen]);

  // Mark as read and track click
  const markAsReadAndTrack = async (alertId) => {
    try {
      // Track click (also marks as read)
      await alertService.trackClick(alertId);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true, clicked: true } : a));
      decrementCount();
    } catch (err) {
      console.error('Error tracking alert click:', err);
      // Fallback to just marking as read
      try {
        await alertService.markAsRead(alertId);
        setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
        decrementCount();
      } catch (e) {
        console.error('Error marking as read:', e);
      }
    }
  };

  // Mark all as read helper
  const markAllAsRead = async () => {
    try {
      await alertService.markAllAsRead();
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      resetCount();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // Dismiss alert helper
  const dismissAlert = async (alertId) => {
    try {
      const alertToRemove = alerts.find(a => a.id === alertId);
      await alertService.dismissAlert(alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      // Decrement count if it was unread
      if (alertToRemove && !alertToRemove.is_read) {
        decrementCount();
      }
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle alert click
  const handleAlertClick = async (alert) => {
    // Mark as read and track click if unread
    if (!alert.is_read) {
      await markAsReadAndTrack(alert.id);
    }

    // Navigate to alert detail page to show full details with sources and analysis
    navigate(`/seller/alert/${alert.id}`);
    setIsOpen(false);
  };

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  // Handle dismiss
  const handleDismiss = async (e, alertId) => {
    e.stopPropagation();
    await dismissAlert(alertId);
  };

  // Get icon component for alert type
  const getIcon = (alert) => {
    const config = ALERT_TYPE_CONFIG[alert.alert_type] || { icon: 'bell' };
    const IconComponent = ICON_MAP[config.icon] || Bell;
    return IconComponent;
  };

  // Get color class for alert type
  const getTypeColor = (alert) => {
    const config = ALERT_TYPE_CONFIG[alert.alert_type] || { color: 'gray' };
    return TYPE_COLORS[config.color] || TYPE_COLORS.gray;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => {
                  navigate('/seller/notifications/settings');
                  setIsOpen(false);
                }}
                className="text-gray-400 hover:text-gray-600"
                title="Notification Settings"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Alert List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              </div>
            ) : alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Bell className="w-8 h-8 mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <ul>
                {alerts.map((alert) => {
                  const IconComponent = getIcon(alert);
                  const typeColor = getTypeColor(alert);

                  return (
                    <li
                      key={alert.id}
                      onClick={() => handleAlertClick(alert)}
                      className={`relative px-4 py-3 border-b border-gray-100 last:border-0 cursor-pointer transition-colors ${
                        !alert.is_read
                          ? 'bg-purple-50 hover:bg-purple-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${typeColor} flex items-center justify-center`}>
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!alert.is_read ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}>
                              {alert.title}
                            </p>
                            <button
                              onClick={(e) => handleDismiss(e, alert.id)}
                              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>

                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {alert.description}
                          </p>

                          <div className="flex items-center gap-2 mt-1">
                            {/* Priority badge */}
                            {alert.priority >= 3 && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${PRIORITY_COLORS[alert.priorityLabel]}`}>
                                {alert.priorityLabel}
                              </span>
                            )}

                            {/* Buyer name if available */}
                            {alert.buyerName && (
                              <span className="text-xs text-gray-500">
                                {alert.buyerName}
                              </span>
                            )}

                            {/* Time */}
                            <span className="text-xs text-gray-400 ml-auto">
                              {formatTimeAgo(alert.created_at)}
                            </span>
                          </div>
                        </div>

                        {/* Unread indicator */}
                        {!alert.is_read && (
                          <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-purple-600"></div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/seller/notifications');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
